import * as React from 'react';
import styled from 'styled-components';
import { colorsV2 } from 'style/colors-v2';
import { Typography } from 'components/DesignSystemV2/Typography';
import { LazyImage } from 'components/LazyImage';
import IconTeacherOverviewGraduateHatSVG from './icons/IconTeacherOverviewGraduateHat';
import IconQuote from './icons/IconQuote';
import { motion, AnimatePresence } from 'framer-motion';

// Dummy data
import ArrowButton from 'components/ArrowButton';
import { toScreen } from 'utils/media-query/responsive.util';
import { useMemo } from 'react';
import { imageFitStyle } from 'style/css-style';

const Main = styled.div`
  display: grid;
  grid-template-columns: auto 460px;
  grid-template-rows: auto;
  align-items: center;
  gap: 48px;

  ${toScreen(1143)} {
    grid-template-columns: repeat(1, 1fr);
    gap: 64px;
  }
`;

const Left = styled.div`
  display: grid;
  gap: 24px;
  height: fit-content;
`;

const Description = styled(Typography)`
  max-width: 440px;
`;

const Info = styled.div`
  display: grid;
  grid-template-columns: 24px auto;
  gap: 12px;
`;

const Right = styled.div`
  display: grid;
  gap: 24px;
`;
const CardWrapper = styled.div`
  position: relative;
  aspect-ratio: 500 / 460;

  ${toScreen(1143)} {
    aspect-ratio: 900 / 460;
  }

  ${toScreen(775)} {
    aspect-ratio: 410 / 460;
  }

  ${toScreen(457)} {
    aspect-ratio: 370 / 460;
  }
`;
const Card = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 24px 32px;
  border-radius: 24px;
  z-index: -1;
  display: flex;
  flex-direction: column;
  border-style: solid;
  border-width: 1px;
  border-color: transparent;
  transform-origin: center;

  &.active {
    z-index: 10;
  }
`;
const QuoteWrapper = styled.div`
  margin-bottom: auto;
`;

const Quote = styled(Typography)`
  position: relative;
`;

const OpenQuote = styled(IconQuote)`
  position: absolute;
  left: 14px;
  top: 28px;
`;

const CloseQuote = styled(IconQuote)`
  transform: translateY(-6px) rotateZ(180deg);
  margin-left: 4px;
`;

const ProfileContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 70px 1fr;
  align-items: center;
  gap: 16px;

  .profile-image {
    width: 60px;
    height: 80px;
    border: 2px solid white;
    border-radius: 12px;
    overflow: hidden;
    transform: rotate(-12deg);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;
const ProfileInfo = styled.div`
  display: grid;
  gap: 4px;
`;

const ButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: 40px 40px;
  gap: 12px;
  justify-content: center;
  align-items: end;
  margin-top: 24px;
`;

const RoleList = styled.ul`
  list-style: ${props => (props.$hideListStyle ? 'none' : 'disc')};
  list-style-position: inside;
  padding: 0;
  margin: 0;
`;

const RoleItem = styled.li`
  margin-bottom: 4px;
  &::marker {
    color: ${colorsV2.neutral120};
  }
`;

const Role = styled(Typography)`
  white-space: pre-line;
  position: relative;
  left: ${props => (props.$hideListStyle ? '0' : '-8px')};
`;

const ImagesGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;

  .image-grid-item {
    border: 3px solid ${colorsV2.white100};
    border-radius: 16px;
    box-shadow: 0px 12px 24px 0px #30427814, 0px 3px 6px 0px #3042780f;
    overflow: hidden;
    aspect-ratio: 120 / 69;
    position: relative;
    flex-basis: 120px;

    &:nth-of-type(odd) {
      transform: rotate(-4deg);
    }

    &:nth-of-type(even) {
      transform: rotate(4deg);
    }

    img {
      ${imageFitStyle('cover')}
    }
  }
`;

const TalkAboutLinearthinking = ({ data }) => {
  const { title, description, quotes, images } = data;
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const TOTAL_QUOTES = quotes.length;
  const VISIBLE_QUOTES = 3;

  const quotesWithListIndex = useMemo(
    () =>
      quotes.map((quote, index) => ({
        ...quote,
        listIndex: index
      })),
    [quotes]
  );

  const visibleQuotes = useMemo(() => {
    const result = [];

    // Add current card and up to 2 more after it
    for (let index = 0; index < VISIBLE_QUOTES; index++) {
      const nextQuote =
        quotesWithListIndex[(currentIndex + index) % TOTAL_QUOTES];
      result.push({
        ...nextQuote,
        position: index // 0 = front, 1 = middle, 2 = back
      });
    }
    return result;
  }, [currentIndex, quotesWithListIndex, TOTAL_QUOTES]);

  function getRotation(position) {
    if (position % VISIBLE_QUOTES === 1) return 5;
    if (position % VISIBLE_QUOTES === 2) return -5;
    return 0;
  }

  function getBackgroundColor(index) {
    switch (index) {
      case 1:
        return colorsV2.green10;
      case 2:
        return colorsV2.yellow10;
      case 3:
        return colorsV2.primary10;
      case 4:
        return colorsV2.purple10;
      default:
        return colorsV2.blue10;
    }
  }

  function getBorderColor(index) {
    switch (index) {
      case 1:
        return colorsV2.green40;
      case 2:
        return colorsV2.yellow40;
      case 3:
        return colorsV2.primary40;
      case 4:
        return colorsV2.purple40;
      default:
        return colorsV2.blue40;
    }
  }

  function getZIndex(index) {
    return VISIBLE_QUOTES - index;
  }

  function handleArrowClick(direction) {
    if (direction === 'left') {
      setCurrentIndex(
        prevIndex => (prevIndex - 1 + TOTAL_QUOTES) % TOTAL_QUOTES
      );
    } else {
      setCurrentIndex(prevIndex => (prevIndex + 1) % TOTAL_QUOTES);
    }
  }

  return (
    <div>
      <Main>
        <Left>
          <Typography
            variant="bold/24-32"
            tabletVariant="bold/32-40"
            desktopVariant="bold/40-48"
            color={colorsV2.black100}
            v3
          >
            {title}
          </Typography>
          <Info>
            <IconTeacherOverviewGraduateHatSVG
              color={colorsV2.neutral100}
              width={24}
              height={24}
            />
            <Description variant="regular/16-24" color={colorsV2.neutral180}>
              {description}
            </Description>
          </Info>
          <ImagesGrid>
            {images.map((image, index) => (
              <LazyImage
                className="image-grid-item"
                key={index}
                handle={image}
                alt="news"
                width={120}
                height={69}
              />
            ))}
          </ImagesGrid>
        </Left>
        <Right>
          <CardWrapper>
            <AnimatePresence initial={false}>
              {visibleQuotes.map(item => (
                <Card
                  key={item.listIndex}
                  initial={{
                    opacity: 0,
                    y: 20,
                    rotateZ: getRotation(item.listIndex),
                    zIndex: getZIndex(item.listIndex)
                  }}
                  animate={{
                    opacity: 1 - item.position * 0.2,
                    y: item.position * 10,
                    rotateZ: getRotation(item.listIndex),
                    zIndex: getZIndex(item.position)
                  }}
                  exit={{
                    opacity: 0,
                    x: -100,
                    transition: { duration: 0.2 }
                  }}
                  transition={{
                    duration: 0.3
                  }}
                  style={{
                    backgroundColor: getBackgroundColor(item.listIndex),
                    borderColor: getBorderColor(item.listIndex)
                  }}
                >
                  <QuoteWrapper>
                    <OpenQuote color={colorsV2.neutral80} />
                    <Quote
                      variant="medium/24-32"
                      color={colorsV2.neutral140}
                      type="span"
                    >
                      {item.quote}
                    </Quote>
                    <CloseQuote color={colorsV2.neutral80} />
                  </QuoteWrapper>
                  <ProfileContainer>
                    <LazyImage
                      className="profile-image"
                      handle={item.profile.image}
                      alt={item.profile.name}
                    />
                    <ProfileInfo>
                      <Typography
                        variant="semi-bold/16-24"
                        color={colorsV2.neutral180}
                      >
                        {item.profile.name}
                      </Typography>
                      <RoleList
                        $hideListStyle={item.profile.roles.length === 1}
                      >
                        {item.profile.roles.map((role, index) => (
                          <RoleItem key={index}>
                            <Role
                              $hideListStyle={item.profile.roles.length === 1}
                              variant="medium/14-20"
                              color={colorsV2.neutral120}
                              type="span"
                            >
                              {role}
                            </Role>
                          </RoleItem>
                        ))}
                      </RoleList>
                    </ProfileInfo>
                  </ProfileContainer>
                </Card>
              ))}
            </AnimatePresence>
          </CardWrapper>
          <ButtonWrapper>
            <ArrowButton
              direction="left"
              onClick={() => handleArrowClick('left')}
            />
            <ArrowButton
              direction="right"
              onClick={() => handleArrowClick('right')}
            />
          </ButtonWrapper>
        </Right>
      </Main>
    </div>
  );
};

export default TalkAboutLinearthinking;
